import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import Decimal from 'decimal.js';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { distinctUntilChanged, take, withLatestFrom } from 'rxjs/operators';
import { AUDIT_TIME_LIST } from 'src/app/reports/components/dea-reports/constants/constants';
import { ReportsService } from 'src/app/reports/reports.service';
import { MM_DD_YYYY } from 'src/app/shared/constants/constants';
import { BaseTableDirective } from 'src/app/shared/directives/base-table.directive';
import { deepCopy, getFormattedDateRangeString, groupByProperty, isEqual, valueIsNumber } from 'src/app/shared/helpers/helpers';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { AuditTimeTypeEnum } from 'src/app/shared/interfaces/AuditTimeTypeEnum';
import { BadgeColorsEnum } from 'src/app/shared/interfaces/BadgeColorsEnum';
import { IAdjustmentPayload } from 'src/app/shared/interfaces/IAdjustmentPayload';
import { AuditStatusesEnum, AuditTypesEnum, IAudit, IAuditDetail } from 'src/app/shared/interfaces/IAudit';
import { IConfirmAuditDetails } from 'src/app/shared/interfaces/IConfirmAuditDetails';
import { IConfirmedPendingTransactionResult } from 'src/app/shared/interfaces/IConfirmedPendingTransactionResult';
import { ITooltipPosition } from 'src/app/shared/interfaces/ITooltipPosition';
import { IValidatedAuditNdc } from 'src/app/shared/interfaces/IValidatedAuditNdc';
import { InputStatesEnum } from 'src/app/shared/interfaces/InputStatesEnum';
import { IPendingTransactionDetails } from 'src/app/shared/interfaces/pending-transaction-details.interface';
import { IPendingTransaction } from 'src/app/shared/interfaces/pending-transaction.interface';
import { NavigatedFromPipe } from 'src/app/shared/pipes/navigated-from/navigated-from.pipe';
import { EventsService } from 'src/app/shared/services/events.service';
import { GetUnfinishedAuditState, SaveUnfinishedAuditState } from 'src/app/shared/states/application/application.actions';
import { ApplicationState } from 'src/app/shared/states/application/application.state';
import { AddNewNdc, CompleteAudit, ConfirmAuditDetail as ConfirmAuditDetails, DecrementBackCountsTotal, GetAuditWithDetails, RemoveNdc, RemovePendingTransactionFromAuditDetail, ValidateDetails } from 'src/app/shared/states/audits/audits.actions';
import { AuditsState } from 'src/app/shared/states/audits/audits.state';
import { AuthState } from 'src/app/shared/states/auth/auth.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { ILocationToExclude } from 'src/app/shared/interfaces/ILocationToExclude.interface';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { FormControls } from 'src/app/shared/utilities/form-controls.util';
import { IAuditDetailFormRaw } from '../interfaces/IAuditDetailFormRaw';
import { IUnconfirmedAuditDetailState, IUnfinishedAuditState } from '../interfaces/unfinished-audit-state.interface';
import { AuditsService } from '../audits.service';
import { GetPendingTransactionDetails, SetWidgetData } from 'src/app/shared/states/transactions/transactions.actions';
import { IConfirmedDetailModel } from '../interfaces/confirmed-audit-detail.interface';
type IAuditDetailFormControl = FormControls<IAuditDetailFormRaw>;
@UntilDestroy()
@Component({
  selector: 'app-audit-details',
  templateUrl: './audit-details.component.html',
  styleUrls: ['./audit-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditDetailsComponent extends BaseTableDirective implements OnInit, OnDestroy {
  tableColumns = {
    friendlyName: true,
    inventoryLocationName: false
  };

  @Select(AuditsState.loading) loading$: Observable<boolean>;
  @Select(AuditsState.success) success$: Observable<boolean>;
  @Select(AuditsState.auditWithDetails) auditWithDetails$: Observable<IAudit>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  @Select(ApplicationState.auditState) auditState$: Observable<IUnfinishedAuditState>;

  auditWithDetailsForm: UntypedFormGroup;

  AuditTypesEnum = AuditTypesEnum;
  AuditStatusesEnum = AuditStatusesEnum;
  BadgeColorsEnum = BadgeColorsEnum;
  ActivityTypesEnum = ActivityTypesEnum;
  ActualCountStatesEnum = InputStatesEnum;
  //adjustments for unconfirmed details
  adjustments: { transactions: IAdjustmentPayload[], notes: IAdjustmentPayload[] } = {transactions: [], notes: [] };
  pendingTransactionsForTooltipsPile: {detailId: string, data: IPendingTransaction[]}[] = [];

  auditWithDetails: IAudit;

  showPinDialog: boolean;
  focusPin: boolean;
  showResetPinDialog: boolean;
  loading: boolean;
  displayInventoryChangedModal: boolean;
  inventoryChangedModalText: string;
  
  //temp var to save temp result from note modal
  currentNoteDetails: any;
  currentNoteAdjustmentQtyInvalid: boolean;
  completeDialogSigner: boolean;
  showConfirmPendingTransactionDialog: boolean;

  iconClasses = '';
  
  math = Math;
  isFileDownloading: boolean;
  navigationState: any;

  pendingDeleteDrugModal$: BehaviorSubject<{show: boolean, idxToRemove: number}> = new BehaviorSubject(null);
  pendingAddDrugModal$: BehaviorSubject<{show: boolean, selectedNdc: any}> = new BehaviorSubject(null);
  drugAlreadyAddedAlert$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  sortBy: 'friendlyName' | 'locationName' | null = null;
  sortAsc: boolean = null;

  rowsIdxToConfirm: number[];

  showLoader: boolean;

  updateFromBodToEodAlert: boolean;
  newNdcAddedAlert: boolean;
  ninetyDayAuditExpiredAlert$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  locationsInHeader: string;

  locationsToExcludeFromSearchScanner: ILocationToExclude[] = [];
  @ViewChild('searchScanner') searchScanner;

  locationsToExcludeFromThePendingTransactionCard: string[];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    protected store: Store,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private auditService: AuditsService,
    private navigatedFromPipe: NavigatedFromPipe,
    private reportService: ReportsService,
    private eventService: EventsService,
    ) {
      super(store);
      this.navigationState = this.router.getCurrentNavigation()?.extras?.state;
      this.resetForm();
     }

  ngOnInit(): void {
    const id = this.route.snapshot.params?.id;
    if (id) {
      let stateRequest: Observable<any> = of(null);
      if (this.store.selectSnapshot(ApplicationState.auditState)?.auditID !== id) {
          //get unfinished details
          stateRequest = this.store.dispatch(new GetUnfinishedAuditState(id));
        }
  
      const auditRequest = this.store.dispatch(new GetAuditWithDetails(id));
    
      forkJoin([stateRequest, auditRequest])
        .pipe(
          untilDestroyed(this)
        )
        .subscribe(() => {
          this.ninetyDayAuditExpiredAlert$.next(this.store.selectSnapshot(AuditsState.response)?.value?.isNoLongerValid);
          this.store.dispatch(new SetWidgetData(null));

          const audit = this.store.selectSnapshot(AuditsState.auditWithDetails);
          if (audit) {
            this.setAudit(audit);
            const state = this.store.selectSnapshot(ApplicationState.auditState);
            // Restore state
            if (state) {
              this.adjustments = deepCopy(state.adjustments as any || []);
              state.ndcs.forEach(x => {
                const detail = this.getDetailGroup(x.id);
                if (detail) {
                  if (!isNaN(x.initialInventoryCount)) {
                    detail.get('initialInventoryCount')?.setValue(x.initialInventoryCount);
                  }
                  detail.get('actualCount')?.setValue(x.actualCount);
                  detail.get('onHand')?.setValue(x.onHand);
                }
              });
              groupByProperty(audit.details.filter(x => !!x.gpi), 'gpi')?.map(x => this.adjustAvailableSOH(x.header));
            }
            this.validateInventory(undefined, true);
            this.cd.detectChanges();
          }
        });
      }

      this.auditWithDetails$
      .pipe(untilDestroyed(this))
      .subscribe(audit => {
          this.setAudit(audit);
        if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled) && audit) {
          const activeLocations = this.store.selectSnapshot(InventoryState.activeLocations).map(x => x.id);
          this.locationsToExcludeFromSearchScanner = this.getDrugsWithLocationsAlreadyInAudit(audit.details);
          if(audit.inventoryLocationIDs) {
            this.locationsToExcludeFromSearchScanner = [...activeLocations.filter(id => !audit?.inventoryLocationIDs?.includes(id)).
               map(id => ({id, drugID: null})), ... this.locationsToExcludeFromSearchScanner];
          }

          const locationNames = Array.from(new Set(audit?.details.map(x => x.inventoryLocationName) || []));

         if (locationNames.length) {
          this.locationsInHeader = locationNames?.reduce((accumulator, currentValue) => accumulator + ', ' + currentValue);
         } else this.locationsInHeader = '';
        }
        
        this.cd.detectChanges();
      });

      this.loading$
      .pipe(untilDestroyed(this))
      .subscribe(loading => this.loading = loading);
      
      this.eventService.logOutClicked$
      .pipe(take(1))
      .subscribe(() => {
        this.preserveState();
      })
  }

  confirmDetails(pin: number) {
    const payload: IConfirmAuditDetails = {
      auditId: this.auditWithDetails.id,
      pin: pin,
      ndcs: []
    };
    
    this.showPinDialog = false;
    
    this.rowsIdxToConfirm.forEach(rowAt => {
      const details = this.auditWithDetailsForm.controls.details as UntypedFormArray;
      const id = details.at(rowAt).get('id').value;
      const actualCountControl = details.at(rowAt).get('actualCount');
      const onHandCount = details.at(rowAt).get('onHand').value;
      if(actualCountControl.valid && +actualCountControl?.value === +onHandCount || !!this.adjustments.notes?.length || !!this.adjustments.transactions?.length) {
        payload.ndcs.push(
          {
            detailId: id, 
            actualCount: actualCountControl?.value,
            transactions: this.adjustments.transactions.filter(x => x.auditDetailId === id)?.map((x: any) => ({...x, purchaseOrderNumber: x?.purchaseOrderNumber || x?.poNumber})),
            notes: this.adjustments.notes.filter(x => x.auditDetailId === id)
        })
      }
    });
    this.store.dispatch(new ConfirmAuditDetails(payload))
    .pipe(untilDestroyed(this), withLatestFrom(this.success$, this.auditWithDetails$))
    .subscribe(([_, success, auditWithDetails]) => {
      if (this.store.selectSnapshot(AuditsState.bulkConfirmNDCsInventoryChanged)) {
        this.handleInvalidNDC(false, 'Transactions have been entered for at least one NDC. Please update actual counts or enter additional adjustments.');
      }
      if(success) {
        (this.store.selectSnapshot(AuditsState)?.recentlyConfirmedDetails as IConfirmedDetailModel[])?.forEach((detail) => {
          const ndc = this.getDetailGroupByLocationIdDrugId(detail?.locationId, detail?.drugId);
          ndc.get('confirmationDate').setValue(detail?.confirmationDate);
          ndc.get('initialInventoryCount').setValue(detail?.initialInventoryCount);
          ndc.get('confirmByFirstName').setValue(detail?.confirmedByFirstName);
          ndc.get('confirmByLastName').setValue(detail?.confirmedByLastName);
        });
        this.cd.detectChanges();
      }
    })
  }

  ndcSelected(ndc: any): void {
    if(this.auditWithDetails.type === AuditTypesEnum.AdHoc) {
      this.continueWithAddNewNdcApi(ndc);
    } else this.pendingAddDrugModal$.next({show: true, selectedNdc: ndc});
  }

  continueWithAddNewNdcApi(ndc: any): void {
    const payload = {
      auditId: this.auditWithDetails.id,
      ndc: ndc?.packageNdc,
      createById: this.store.selectSnapshot(AuthState)?.currentUser?.id,
      friendlyName: ndc?.friendlyName,
      onHand: ndc?.onHand,
      drugId: ndc?.drugID,
      locationId: ndc?.locationID,
      inventoryLocationName: ndc?.locationName,
      gpi: ndc.gpi,
      similarOnHand: ndc.similarOnHand
    };

    this.store.dispatch(new AddNewNdc(payload))
    .pipe(untilDestroyed(this), withLatestFrom(this.success$))
    .subscribe(([_, success]) => {
      if(success) {
        if(!this.store.selectSnapshot(AuditsState)?.createAuditDetailDrugIsAlreadyAddedResponse) {
          this.insertIntoFormNewDetail(ndc, this.store.selectSnapshot(AuditsState)?.lastAddedAuditDetailId);
        } else {
          this.drugAlreadyAddedAlert$.next(true);
        }
        this.searchScanner?.resetSearch();
      }
    });
  }

  addingConfirmed(selectedNdc: any): void {
    this.continueWithAddNewNdcApi(selectedNdc);
    this.pendingAddDrugModal$.next(null);
  }

  insertIntoFormNewDetail(detail: any, id: number): void {
    const details = this.getDetailControls();
    if((details?.value as any[])?.map(x => x?.id)?.indexOf(id) > -1)
      return;
    const newDetail = this.fb.group({
        id: id,
        gpi: detail.gpi,
        drugId: detail?.drugID || detail?.drugId,
        friendlyName: detail?.friendlyName,
        packageNDC11Digit: detail?.packageNDC11Digit || detail?.packageNdcs?.at(-1),
        onHand: detail?.onHand,
        actualCount: detail?.actualCount || '',
        actualCountState: InputStatesEnum.None,
        initialInventoryCount: detail?.initialInventoryCount !== null && detail?.initialInventoryCount !== undefined ? detail?.initialInventoryCount : detail?.onHand,
        confirmationDate: detail?.confirmationDate,
        confirmByFirstName: detail?.confirmByFirstName,
        confirmByLastName: detail?.confirmByLastName,
        start: detail?.start,
        in: detail?.in,
        out: detail?.out,
        inventoryLocationID: detail?.inventoryLocationID ||  detail?.locationID,
        inventoryLocationName: detail?.inventoryLocationName,
        similarOnHand: detail?.similarOnHand,
        availableSOH: detail?.similarOnHand
      });

      newDetail.controls.actualCount.setValidators([Validators.min(detail?.onHand), Validators.max(detail?.onHand)]);

      //value changes in unconfirmed details
      if(!detail?.confirmationDate) {
        newDetail.controls.actualCount.valueChanges
        .pipe(
          untilDestroyed(this),
          distinctUntilChanged()
          )
        .subscribe(data => {
          this.onActualControlChanged(newDetail.controls.actualCount);
        })
      }
      details.push(newDetail);
  }

  private adjustAvailableSOH(gpi: string) {
    if (!gpi)
      return;

    const rows = this.getDetailControls()
    .controls.filter(control => control.value.gpi === gpi);
      if (rows?.length) {
        const actualSOH = new Decimal(+rows[0].value.similarOnHand);

          const sohAfterAdjustments =  this.adjustments.transactions.filter(x => x.gpi === gpi)
          ?.map(x => x.activityType === ActivityTypesEnum.Received ? x.adjustmentQty : +x.adjustmentQty * -1)
          .reduce((acc, count) => acc.add(new Decimal(count)), actualSOH)
          .toNumber();

          const sohAfterNotes = this.adjustments.notes.filter(x => x.gpi === gpi)
          ?.map(x => x.activityType === ActivityTypesEnum.Received ? x.adjustmentQty : +x.adjustmentQty * -1)
          .reduce((acc, count) => acc.add(new Decimal(count)), new Decimal(sohAfterAdjustments))
          .toNumber();
      
            rows.forEach(control => {
              const value = control.value;
              if (!!value.gpi) {
                control.get('availableSOH').setValue(sohAfterNotes);
              }
            });
      }
    }

    private getDetailControls(): FormArray<FormGroup<IAuditDetailFormControl>> {
        return this.auditWithDetailsForm.controls.details as FormArray<FormGroup<IAuditDetailFormControl>>;
    }
  

  onActualControlChanged(actualCount: AbstractControl, suppressInitialState?: boolean) {
     const group = actualCount?.parent;
     const location = group?.controls['inventoryLocationID']?.value;
     const ndc = group?.controls['packageNDC11Digit']?.value;
     const positionInArray = this.getDetailPositionInArray(location, ndc);
     const actualCountState = this.getActualCountStateControl(positionInArray);
     if(actualCount.invalid) 
        actualCountState.setValue(InputStatesEnum.Error);
     else if(actualCount?.value && actualCount?.valid)
        actualCountState.setValue(InputStatesEnum.ReadyForConfirmation);
     else if(!actualCount?.value && !suppressInitialState)
        actualCountState.setValue(InputStatesEnum.Initial);
    
    if(actualCount?.value === '') {
      const id = group?.get('id')?.value;
      this.getDetailGroup(id)?.get('onHand')?.setValue(group?.get('initialInventoryCount')?.value);
      this.adjustments.transactions = this.adjustments.transactions.filter(x => x.auditDetailId !== id);
      this.adjustments.notes = this.adjustments.notes.filter(x => x.auditDetailId !== id);
    }

    this.cd.detectChanges();
  }

  pendingTransactionExists(detaildId: string): boolean {
   return !!this.pendingTransactionsForTooltipsPile?.find(x => x.detailId === detaildId)?.data?.length;
  }

  removeDetail(i: number) {
    if(this.loading) return;

    if(this.auditWithDetails.type === AuditTypesEnum.AdHoc) {
      this.continueWithDeleteDetailApi(i);
    } else this.pendingDeleteDrugModal$.next({show: true, idxToRemove: i});
  }

  private continueWithDeleteDetailApi(rowNumber: number) {
   const id = this.getFormGroup(rowNumber)?.get('id')?.value;
    this.store.dispatch(new RemoveNdc(id))
    .pipe(untilDestroyed(this), withLatestFrom(this.success$))
    .subscribe(([_, succcess]) => {
      if(succcess) {
        this.deleteRowFromFormArray(rowNumber);
        this.cd.detectChanges();
      }
    });
  }

  deletingConfirmed(rowNumber: number) {
    this.continueWithDeleteDetailApi(rowNumber);
    this.pendingDeleteDrugModal$.next(null);
  }

  private deleteRowFromFormArray(i: number): void {
    const details = this.getDetailControls();
    const gpi = details.value[i].gpi;
    details.removeAt(i);
    this.resetForm();
    this.auditWithDetailsForm.controls.details = details;
    this.auditWithDetailsForm.updateValueAndValidity();
  }

  calcDiscrepancy(rowData: IAuditDetail): number {
   // return rowData?.actualCount - rowData?.initialInventoryCount;
   return rowData?.actualCount - rowData?.onHand;
  }

  onFocus(rowNumber: number): void {
    const actualCount = this.getActualCountControl(rowNumber);
    const actualCountState = this.getActualCountStateControl(rowNumber);
    const id = this.getId(rowNumber);

    if(!actualCount.value?.toString()?.length) 
      actualCountState.setValue(InputStatesEnum.Initial);
    else if (!isNaN(actualCount.value) && actualCount.valid && !this.pendingTransactionExists(id))
      actualCountState.setValue(InputStatesEnum.ReadyForConfirmation);

    this.cd.detectChanges();
  }

  onBlur(rowNumber: number): void {
    const actualCount = this.getActualCountControl(rowNumber);
    const actualCountState = this.getActualCountStateControl(rowNumber);
    const id = this.getId(rowNumber);
    if(!actualCount.value?.toString()?.length)
      actualCountState.setValue(InputStatesEnum.None);
    else if (!isNaN(actualCount.value) && actualCount.valid && !this.pendingTransactionExists(id))
      actualCountState.setValue(InputStatesEnum.Pending);

    this.cd.detectChanges();
  }

  focusNextInput(rowNumber: number) {
    document.querySelector(`[ng-reflect-name="${rowNumber + 1}"]`)?.querySelector('input')?.focus();
  }

  getIcon(state: InputStatesEnum): string {
    this.iconClasses = '!top-[9px]';
    const defaultIconSize = ' !right-[9px] !h-[18px] !w-[18px]';
    const errorIconSize =' !right-[11px] !h-[18px] !w-[16px]';
    let iconName = '';
    switch (state) {
      case InputStatesEnum.Initial:
        iconName = 'done-grey';
        break;
      case InputStatesEnum.ReadyForConfirmation:
        iconName = 'done-blue';
        break;
      case InputStatesEnum.Pending:
        iconName = 'done-yellow';
        break;
      case InputStatesEnum.Error:
        this.iconClasses.replace(defaultIconSize, '');
        this.iconClasses += errorIconSize;
        return'cancel';
      default:
        break;
    }
    this.iconClasses.replace(errorIconSize, '');
    this.iconClasses += defaultIconSize;
    return iconName;
  }

  getPendingTransactionIconPath(id: string): string {
    return !this.pendingTransactionExists(id) ?  
    'assets/icons/icon-clock-white.svg' :
    'assets/icons/icon-clock-yellow.svg';
  }

  trackByIndex(index, item) {
    return index;
  }

  onInputIconClick(rowNumber: number) {
    const ndcControl = this.getFormGroup(rowNumber);
    if (ndcControl.valid && valueIsNumber(ndcControl.get('actualCount')?.value)) {
      this.validateInventory(rowNumber);
    }
  }
  validateInventory(rowNumber?: number, allUnconfirmedNDCs?: boolean) {
    this.completeDialogSigner = false;
    this.rowsIdxToConfirm = [];
    const unconfirmedDetailsControl = this.auditWithDetailsForm.controls.details as UntypedFormArray;
    //Define what to validate
    if (allUnconfirmedNDCs) { //validate all unconfirmed NDCs
      const allUnconfirmed = this.getAllUnconfirmedNdcs();
      this.rowsIdxToConfirm.push(...allUnconfirmed.map(x => this.getDetailPositionInArray(x.drugId.toString(), x.locationId)));
    } else if(!rowNumber) {//validate all ready for confirmation
      const ndcsToConfirm = this.findReadyForConfirmationRows()?.map(detailControl => ({ locationId: detailControl?.value?.inventoryLocationID, ndc: detailControl?.value?.packageNDC11Digit }));
      this.rowsIdxToConfirm.push(...ndcsToConfirm.map(x => this.getDetailPositionInArray(x.ndc, x.locationId)));
      this.showLoader = ndcsToConfirm.length > 1;
    } else { //validate concrete ndc
      const id = this.getId(rowNumber);
      this.rowsIdxToConfirm = [rowNumber]; 
    }
    
    if(!this.rowsIdxToConfirm?.length) return;
    let inventoriesToValidate = this.rowsIdxToConfirm.map(idx => ({
      drugID: unconfirmedDetailsControl?.at(idx)?.get('drugId').value, 
      locationID: unconfirmedDetailsControl?.at(idx)?.get('inventoryLocationID').value
    }));
    
    let payload = { inventories: inventoriesToValidate, ninetyDayAuditId: null, to: null};

    if(this.auditWithDetails.type === AuditTypesEnum.NinetyDayAudit || this.auditWithDetails.type === AuditTypesEnum.TotalsOnly) {
      payload.ninetyDayAuditId = this.auditWithDetails.id;
      payload.to = this.auditWithDetails.to;
    }

    this.store.dispatch(new ValidateDetails(payload))
    .pipe(untilDestroyed(this), withLatestFrom(this.success$))
    .subscribe(([_, isSuccess]) => {
      if (!isSuccess) return;
       this.handleInvalidNDC(allUnconfirmedNDCs, null);
    });
  }

  private handleInvalidNDC(allUnconfirmedNDCs: boolean, errorMessage?: string): void {
    let invalidInventoriesCount = 0;
    (this.store.selectSnapshot(AuditsState)?.validatedAuditDetails as IValidatedAuditNdc[])?.forEach(mostRecentInventory => {
      const idx = this.getDetailPositionInArray( mostRecentInventory.drugID.toString(), mostRecentInventory.locationID);
      const detailControl = (this.auditWithDetailsForm.controls.details as UntypedFormArray)?.at(idx);
      const initialInventoryCount = detailControl?.get('initialInventoryCount');

      if(mostRecentInventory.onHand !== initialInventoryCount?.value) {
        invalidInventoriesCount++;
        //update each row with new values and clear adjustments
        initialInventoryCount?.setValue(mostRecentInventory.onHand);
        detailControl.get('onHand').setValue(mostRecentInventory.onHand);
        detailControl.get('actualCount').setValidators([Validators.min(mostRecentInventory.onHand), Validators.max(mostRecentInventory.onHand)]);
        detailControl.get('actualCount').updateValueAndValidity();
        if(this.auditWithDetails.type === AuditTypesEnum.NinetyDayAudit || this.auditWithDetails.type === AuditTypesEnum.TotalsOnly) {
          detailControl.get('in').setValue(mostRecentInventory.in);
          detailControl.get('out').setValue(mostRecentInventory.out);
        }
        this.onActualControlChanged(detailControl?.get('actualCount'));
        this.adjustments.transactions = this.adjustments.transactions.filter(x => x.auditDetailId !== detailControl.get('id').value);
        this.adjustments.notes = this.adjustments.notes.filter(x => x.auditDetailId !== detailControl.get('id').value);
      }
    });

    if(invalidInventoriesCount === 0 && !allUnconfirmedNDCs)  {
      this.openPinDialog();
    } else if(invalidInventoriesCount === 1 && !allUnconfirmedNDCs) {
      this.inventoryChangedModalText = errorMessage?? 'A transaction has been entered for this drug, please update actual count or enter additional adjustments.';
      this.displayInventoryChangedModal = true;
    }
     else if (invalidInventoriesCount > 1 && !allUnconfirmedNDCs) {
      this.inventoryChangedModalText = errorMessage ?? 'Transactions have been entered for at least one NDC. Please update actual counts or enter additional adjustments.';
      this.displayInventoryChangedModal = true;
    }

    if ((this.auditWithDetails.type === AuditTypesEnum.NinetyDayAudit || this.auditWithDetails.type === AuditTypesEnum.TotalsOnly)
      && this.auditWithDetails?.endDateType === AuditTimeTypeEnum.BeginningOfDay && invalidInventoriesCount > 0) { 
      this.updateFromBodToEodAlert = true;
      this.auditWithDetails.endDateType = AuditTimeTypeEnum.EndOfDay;
    }

    this.cd.detectChanges();
  }

  private getDetailPositionInArray(ndcToken: string, locationID: string): number {
    return (this.auditWithDetailsForm.controls.details as UntypedFormArray).controls
    .findIndex(x => Object.values(x.value).includes(locationID) && Object.values(x.value).includes(ndcToken));
  }

  private findReadyForConfirmationRows(): AbstractControl[] {
    const details = (this.auditWithDetailsForm.controls.details as UntypedFormArray);
    return (details?.controls as AbstractControl[]).filter(x => {
      const confirmed = x?.get('confirmationDate');
      const actualCountControl = x?.get('actualCount');
      const onHand = x?.get('onHand')?.value;
      return !confirmed?.value && valueIsNumber(actualCountControl?.value) && (+actualCountControl?.value - +onHand) === 0;
    });
  }

  private getAllUnconfirmedNdcs(): {locationId: string, drugId: string}[] {
    const details = (this.auditWithDetailsForm.controls.details as UntypedFormArray);
    return details ? (details?.controls as AbstractControl[]).filter(x => !x?.get('confirmationDate').value)
    ?.map((x) => ({locationId: x?.value?.inventoryLocationID, drugId: x?.value.drugId})) : [];
  }

  openPinDialog() {
    this.showPinDialog = true;
    this.focusPin = true;
  }

  closeDialog(): void {
    this.showPinDialog = false;
    this.focusPin = false;
  }

  validatePin({success, pin}): void {
    if (success) {
      if(!this.completeDialogSigner)
        this.confirmDetails(pin);
      else {
        this.store.dispatch(new CompleteAudit({id: this.auditWithDetails.id, pin: pin}))
        .pipe(take(1), withLatestFrom(this.success$))
        .subscribe(([_, success]) => {
          if (!success) {
            this.newNdcAddedAlert = true;
          } else if (this.auditWithDetails.type === AuditTypesEnum.BackCount) {
            this.store.dispatch(new DecrementBackCountsTotal());
          }
          this.completeDialogSigner = false;  
          this.showPinDialog = false;
          this.cd.detectChanges();
        })
      }
    }
  }

  openResetPinDialog(): void {
    this.showPinDialog = false;
    this.focusPin = false;
    this.showResetPinDialog = true;
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;
    this.showPinDialog = true;
    setTimeout(() => this.focusPin = true);
  }

  afterPinUpdated(success: boolean): void {
    if (success) {
      this.closeResetPinDialog();
    }
  }

  atLeastOneValid(): boolean {
    return this.findReadyForConfirmationRows()?.length > 0;
  }

  adjustmentAddedHandler(adjustment: IAdjustmentPayload) {
    adjustment.adjustmentQty = (adjustment as any)?.addedQuantity;
    adjustment.inventoryLocationID = (adjustment as any)?.drugs[0]?.inventoryLocationId;
    adjustment.gpi = (adjustment as any)?.drugs[0]?.gpi;
    this.updateOnHandQtyWithAdjustedQty(adjustment.auditDetailId, adjustment.adjustmentQty, (adjustment as any)?.drugs[0]?.gpi, 'add');
    adjustment.createDate = new Date();
    this.adjustments?.transactions.push(adjustment);
    this.adjustAvailableSOH(adjustment.gpi);
  }

  getConfirmedAdjustmentNumberSign(auditDetailId: string): string {
    const detail = this.auditWithDetails.details.find(x => x.id.toString() === auditDetailId);
    return +detail?.initialInventoryCount > +detail?.actualCount ? '-' : '+';
  }


  getAddedAdjustmentNumberSign(auditDetailId: string): string {
    const detail = this.getAuditDetailValue(auditDetailId);
    return +detail?.initialInventoryCount > +detail?.actualCount ? '-' : '+';
  }

  calcAddedDiscrepancy(auditDetailId: string): number | string  {
    const detail = this.getAuditDetailValue(auditDetailId);
    if(detail?.actualCount || detail?.actualCount?.length) {
      return (new Decimal(+detail?.actualCount).sub(new Decimal(+detail?.onHand)).toDecimalPlaces(3).toNumber());
    }
    return '';
  }

  calcAddedAdjustments(auditDetailId: string): number {
    const transactions = this.adjustments?.transactions?.filter(x => x.auditDetailId === auditDetailId)?.reduce((accumulator, currentValue) => accumulator + +currentValue?.adjustmentQty, 0);
    const notes = this.adjustments?.notes?.filter(x => x.auditDetailId === auditDetailId)?.reduce((accumulator, currentValue) => accumulator + +currentValue?.adjustmentQty, 0);
    return transactions + notes;
  }
  
  calcLeftDiscrepancyToAdjust(auditDetailId: string) {
    return Math.abs(+this.calcAddedDiscrepancy(auditDetailId)); 
  }

  calcAdjustments(detailId: number): number {
    const detail = this.auditWithDetails?.details?.find(x => x.id === detailId);
    const transactionsAdjustment = detail?.adjustments?.map(x => x?.adjustmentQty)?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    const notesAdjustment = detail?.notes?.map(x => x?.adjustmentQty)?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return  Math.abs(transactionsAdjustment) + Math.abs(notesAdjustment);
  }

  getConfirmedDetailTooltipData(detailId: number): any {
    const detail = this.auditWithDetails?.details?.find(x => x.id === detailId);
    return {
      transactions: detail?.adjustments,
      notes: detail?.notes
    };
  }

  getDetailAdjustments(auditDetailId: string) {
    return {
      transactions: this.adjustments?.transactions?.filter(x => x.auditDetailId === auditDetailId), 
      notes: this.adjustments?.notes?.filter(x => x.auditDetailId === auditDetailId),
      withRemoveControl: true
    };
  }

  removeAdjustmentHandler(eventData: any) {
    this.removeAdjustment(eventData?.rowNumber, eventData?.detailId, eventData?.type);
  }

  removeAdjustment(i: number, auditDetailId: string, adjustmentType: 'transactions' | 'notes') {
    const detail = this.adjustments?.[adjustmentType]?.filter(x => x.auditDetailId === auditDetailId);
    const gpi = detail[i].gpi;
    this.updateOnHandQtyWithAdjustedQty(auditDetailId, detail[i]?.adjustmentQty, detail[i]?.gpi, 'remove');
    detail.splice(i, 1);
    this.adjustments[adjustmentType] = this.adjustments?.[adjustmentType]?.filter(x => x.auditDetailId !== auditDetailId).concat(detail);
    this.adjustAvailableSOH(gpi);
  }

  addNewNote(val) {
    this.currentNoteDetails = {
      drugID: val?.drugId, 
      friendlyName: val?.friendlyName, 
      packageNdc: val?.packageNDC11Digit, 
      onHand: val?.onHand,
      auditDetailId: val?.id,
      gpi: val?.gpi,
      isNoteAdding: val?.actualCount > val?.onHand
    };
  }

  noteModalSubmitted(data: any): void {
    this.adjustments.notes.push({
      auditDetailId: this.currentNoteDetails?.auditDetailId, 
      adjustmentQty: data?.adjustmentQty, 
      discrepancyDetails: data?.noteDescription,
      createDate: new Date(),
      activityType: this.currentNoteDetails.isNoteAdding ? ActivityTypesEnum.Received : ActivityTypesEnum.Dispensed,
      gpi: this.currentNoteDetails.gpi
    });

    this.updateOnHandQtyWithAdjustedQty(this.currentNoteDetails?.auditDetailId, +data?.adjustmentQty, this.currentNoteDetails?.gpi, 'add');
    this.adjustAvailableSOH(this.currentNoteDetails.gpi);
    this.currentNoteDetails = null;
  }

  openConfirmPendingTransactionsModal(id?: number, locationsToExclude?: string[]) {
    this.locationsToExcludeFromThePendingTransactionCard = locationsToExclude;
    this.store.dispatch(new GetPendingTransactionDetails(id))
    .pipe(take(1)).subscribe(() => {
     this.showConfirmPendingTransactionDialog = true;
     this.cd.detectChanges();
    });
  }
  /**
   * Removes a confirmed pending transaction from all locations and updates the audit detail accordingly.
   */
  pendingTransactionConfirmedHandler(result: IConfirmedPendingTransactionResult) {
    this.getIndexesOfTheConfirmedPendingTransactionPositions(result.id)?.forEach(idx => {
      if(idx > -1) { //remove item from tooltip list
        this.pendingTransactionsForTooltipsPile[idx].data = this.pendingTransactionsForTooltipsPile[idx]?.data?.filter(x => x.id !== result.id);
      }
    });

    this.store.dispatch(new RemovePendingTransactionFromAuditDetail(result?.drugId, result?.id));

    if(!result.isPreviouslyConfirmedOrDeleted) {
      //Adjust audit detail after confirmation
      const detailGroup = this.getDetailGroupByLocationIdDrugId(result.locationId, result.drugId.toString());
      const onHandControl = detailGroup.get('onHand');
      const initialInventoryCountControl = detailGroup.get('initialInventoryCount');
      const actualCountControl = detailGroup.get('actualCount');
      onHandControl.setValue(+onHandControl?.value + result.addedQuantity);
      initialInventoryCountControl.setValue(+initialInventoryCountControl?.value + result.addedQuantity);

      //Deduct from onHand since the confirmed pending card affected inventory
      if (+actualCountControl.value >= Math.abs(+result.addedQuantity)) {
        actualCountControl.setValue(+actualCountControl.value - Math.abs(+result.addedQuantity));
      }

      actualCountControl.setValidators([Validators.min(onHandControl?.value), Validators.max(onHandControl?.value)]);
      this.onActualControlChanged(actualCountControl);
    }
  }
  //the same pending transaction is shared across locations
  private getIndexesOfTheConfirmedPendingTransactionPositions(confirmedPendingTransactionId: number): number[] {
   return this.pendingTransactionsForTooltipsPile?.map((x, index) => x.data.some(x => x.id === confirmedPendingTransactionId) ? index : -1);
  }

  pendingTransactionUpdated(pendingTransaction: IPendingTransactionDetails) {
    this.getIndexesOfTheConfirmedPendingTransactionPositions(pendingTransaction?.id)?.forEach(idx => {
      if(idx > -1) {
      const transaction = this.pendingTransactionsForTooltipsPile[idx]?.data?.find(x => x.id === pendingTransaction.id);
      if (transaction?.drugId !== pendingTransaction.drugId) {  //remove item from tooltip list because NDC changed
        this.pendingTransactionsForTooltipsPile[idx].data = this.pendingTransactionsForTooltipsPile[idx]?.data?.filter(x => x.id !== pendingTransaction.id);

        //find out if new ndc is being audited
        const detailGroups = this.getDetailGroupsByDrugId(pendingTransaction.drugId.toString());
        if (detailGroups?.length) {
          detailGroups.forEach(detailGroup => {
            const pendingTransactionInList = this.pendingTransactionsForTooltipsPile.find(x => x.detailId === detailGroup.get('id').value);
            if(!pendingTransactionInList) {
            this.pendingTransactionsForTooltipsPile.push({
              detailId: detailGroup.get('id').value,
              data: [{ ...pendingTransaction, addedQuantity: pendingTransaction.count, inventoryOnHand: pendingTransaction.starting }]
            });
            } else {
              pendingTransactionInList.data.push({ ...pendingTransaction, addedQuantity: pendingTransaction.count, inventoryOnHand: pendingTransaction.starting });
            }
          });
        }
      }
    }
  });
}


  print(): void {
    this.isFileDownloading = true;
    this.auditService.downloadAudit(this.auditWithDetails.id, new Date().getTimezoneOffset() / 60, this.sortBy, this.sortAsc, 
    this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs),
    this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)
  )
    .pipe(untilDestroyed(this))
      .subscribe(res => {
        const name = 'Audit Report ' + new Date().toDateString() + '.pdf';
        this.auditService.handleDownload(res, name);

        this.isFileDownloading = false;
        this.cd.markForCheck();
      }, err => {
        this.isFileDownloading = false;
        this.cd.markForCheck();
      });
  }

  onCompleteAudit(): void {
    this.completeDialogSigner = true;
    this.openPinDialog();
  }

  getPendingTransactionsCount(detailId: string): number {
    return this.pendingTransactionsForTooltipsPile?.find(x => x.detailId == detailId)?.data?.length;
  }

  getDetailPendingTransactions(row: IAuditDetailFormRaw): any {
    return { 
      transactions: this.pendingTransactionsForTooltipsPile?.find(x => x.detailId == row.id)?.data, 
      locationsToExclude: this.store.selectSnapshot(InventoryState.activeLocations)?.filter(x => x.id !== row.inventoryLocationID)?.map(x => x.id)
    };
  }

  shouldDisplayPendingTransactions(row: IAuditDetailFormRaw): boolean {
    return !row.confirmationDate && this.getPendingTransactionsCount(row.id) > 0 && 
    row.inventoryLocationID !== this.store.selectSnapshot(InventoryState.damagedExpiredLocationID);
  }

  getConfirmedDetailsCount(): number {
   return this.auditWithDetails?.details?.filter(x => x?.confirmationDate)?.length;
  }

  sortColumn(column: string) {
    const unconfirmedDetailsFormArray = this.auditWithDetailsForm.controls.details as UntypedFormArray;
    const sorted = this.sort(unconfirmedDetailsFormArray?.value, column);
    this.resetForm();
    sorted?.map(x => this.insertIntoFormNewDetail(x, x.id));
    unconfirmedDetailsFormArray.controls?.map(x => this.onActualControlChanged(x.get('actualCount'), true));
    
    switch(column) {
      case('friendlyName'): {
        this.sortBy = 'friendlyName';
        this.sortAsc = this.tableColumns.friendlyName;
        break;
      }
      case('inventoryLocationName') : {
        this.sortBy = 'locationName';
        this.sortAsc = this.tableColumns.inventoryLocationName;
        break;
      }
    }
  }

  private setAudit(audit: IAudit): void {
    this.auditWithDetails = {...audit};
    this.pendingTransactionsForTooltipsPile = this.auditWithDetails.details?.filter(x => x?.pendingTransactions?.length > 0).map(x => { return {detailId: x.id.toString(), data: x.pendingTransactions}});
    audit?.details?.map(x => this.insertIntoFormNewDetail(x, x.id));    
  }

  private updateOnHandQtyWithAdjustedQty(auditDetailId: string, addedQuantity: number, gpi: string, actionType: 'add' | 'remove') {
    const onHand = this.getDetailGroup(auditDetailId).get('onHand');
    const onHandBefore = onHand?.value;
    let discrepancy = this.calcAddedDiscrepancy(auditDetailId);
    if(discrepancy === 0) {
      const initialExpected = (this.store.selectSnapshot(AuditsState)?.auditWithDetails as IAudit)?.details?.filter(x => x.id.toString() === auditDetailId)[0]?.initialInventoryCount;
      discrepancy = onHandBefore - initialExpected;
    }
    let addedQuantityWithSign = +addedQuantity;
    if((discrepancy as number > 0 && actionType === "remove") || (discrepancy as number < 0 && actionType === "add")) {
       addedQuantityWithSign = addedQuantity * -1;
    } 
    
    onHand.setValue(onHandBefore + addedQuantityWithSign);
  }

  private getAuditDetailValue(auditDetailId: string) {
    const details = (this.auditWithDetailsForm.controls.details as FormArray<FormGroup<IAuditDetailFormControl>>)?.value as any[];
    return details?.filter(x => x?.id === auditDetailId)[0];
  }

  private getDetailGroup(auditDetailId: string): AbstractControl {
    const details = (this.auditWithDetailsForm.controls.details as FormArray<FormGroup<IAuditDetailFormControl>>).controls.find(x => x.value.id == auditDetailId);
    return details;
  }

  private getDetailGroupByLocationIdDrugId(locationId: string, id: string) {
    const details = (this.auditWithDetailsForm.controls.details as FormArray<FormGroup<IAuditDetailFormControl>>)
    .controls.find(x => x.value.inventoryLocationID === locationId && x.value.drugId === id);
    return details;
  }

  private getDetailGroupsByDrugId(id: string): FormGroup<FormControls<IAuditDetailFormRaw>>[] {
    const details = (this.auditWithDetailsForm.controls.details as FormArray<FormGroup<IAuditDetailFormControl>>)
    .controls.filter(x => x.value.drugId === id);
    return details;
  }
  
  private getActualCountStateControl(rowNumber: number): AbstractControl {
    return this.getFormGroup(rowNumber).get('actualCountState');
  }

  private getActualCountControl(rowNumber: number): AbstractControl {
    return this.getFormGroup(rowNumber).get('actualCount');
  }

  private getId(rowNumber: number): string {
    return this.getFormGroup(rowNumber).get('id')?.value;
  }

  private getFormGroup(rowNumber: number): FormGroup<IAuditDetailFormControl> {
    const details = this.auditWithDetailsForm.controls.details as FormArray<FormGroup<IAuditDetailFormControl>>;
    return details?.at(rowNumber)
  }

  private resetForm(): void {
    this.auditWithDetailsForm = this.fb.group({
      details: this.fb.array([])
    });
  }

  getAuditTypeSuffix(): string {
    const fromTimeType = AUDIT_TIME_LIST?.find(x => x.id === this.auditWithDetails?.startDateType)?.name;
    const toTimeType = AUDIT_TIME_LIST?.find(x => x.id === this.auditWithDetails?.endDateType)?.name;
    let title = getFormattedDateRangeString(this.auditWithDetails?.from, this.auditWithDetails?.to, MM_DD_YYYY, 'from', '-', fromTimeType, toTimeType);
    return this.reportService.composeReportTitleWithScheduleExtension(title, this.auditWithDetails?.schedules);
  }

  getPositionForPendingTransactionTooltip(): ITooltipPosition {
    return {bottom: {x: '-149px', y: '32px'},
            right: {x: '58px', y: '-52px'}};
  }
  
  getPositionForDiscrepancyTooltip(): ITooltipPosition {
    return {bottom: {x: '-141px', y: '22px'}};
  }

  navigateBack() {
    const navigatedFrom = this.navigatedFromPipe.transform(this.navigationState, 'Audits', '/audits')?.link;
    this.router.navigate([navigatedFrom], {state: {
      isNavigatedBack: true
    }});
  }

  private preserveState(): void {
    if (!this.auditWithDetails) 
      return;

    const unconfirmedDetails: IUnconfirmedAuditDetailState[] = ((this.auditWithDetailsForm.controls.details as UntypedFormArray).value as any[])
    ?.filter(x => !x.confirmationDate && x?.actualCount)
    ?.map(x => ({id: x.id, actualCount: x.actualCount, actualCountState: x.actualCountState, onHand: x.onHand, initialInventoryCount: x.initialInventoryCount}));
    const state: IUnfinishedAuditState = {
      auditID: this.auditWithDetails.id, 
      ndcs: unconfirmedDetails,
      adjustments: this.getUnconfirmedAdjustments() // ignore confirmed adjustments
    }
    const prevState = this.store.selectSnapshot(ApplicationState.auditState);
    if (!isEqual(prevState, state)) {
      this.store.dispatch(new SaveUnfinishedAuditState(state));
    }
  }

  private getDrugsWithLocationsAlreadyInAudit(details: IAuditDetail[]): ILocationToExclude []  {
    return details?.map(x => ({id: x?.inventoryLocationID, drugID: x?.drugId.toString()}));
  }

  private getUnconfirmedAdjustments(): any {
    const details = this.store.selectSnapshot(AuditsState.auditWithDetails).details;
    const unconfirmedAdjustmentsPredicate = (adj: IAdjustmentPayload) => details.findIndex(x => x.id.toString() === adj.auditDetailId && !!x.confirmByFirstName) === -1;

    const unconfirmedTransactions = this.adjustments?.transactions
    .filter(unconfirmedAdjustmentsPredicate);
    const unconfirmedNotes = this.adjustments?.notes
    .filter(unconfirmedAdjustmentsPredicate);
    
    return {
      transactions: unconfirmedTransactions,
      notes: unconfirmedNotes
    }
  }

  ngOnDestroy(): void {
    this.preserveState();
  }

}
